/* eslint-disable react/display-name,no-unused-vars */
import axios from 'helpers/axios';
import {
  useCancelToken,
  useCompany,
  useDashboard,
  useMutableRef,
  useOnFirstRender,
  useOpenState,
  useStateRef,
  useToasts
} from 'hooks';
import React from 'react';
import { useDrop } from 'react-dnd';
import {
  CardGrid,
  DropDownMenu,
  DropDownMenuItem,
  EmailModal,
  FloatingMenuButton,
  FloatingMenuChildButton,
  Icon,
  JobDot
} from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import { Container, ModalPosition } from 'ui/components/styled';
import { Modal } from 'ui/components/styled/NewModal';
import { ConfirmationModal } from 'ui/components/styled/NewModal/ConfirmationModal';
import Text from 'ui/components/styled/Text';
import { AddCategory, AddCustomer, AddJob, EditCategory, EditCustomer } from '../forms';
import { buildFilter } from './helper';
import { Card, CardTitle, CategoryLabel } from './styled';

export const CardView = React.forwardRef(
  (
    {
      headerActions,
      isFullScreenActive,
      filterValue,
      openMobileFilter,
      activeStatus = '',
      onFilterChange,
    },
    ref
  ) => {
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [total, setTotal] = React.useState(0);
    const [perPage] = React.useState(20);
    const [search, setSearch] = React.useState(filterValue);
    const [advancedSearchOpen, setAdvancedSearchOpen] = React.useState(false);

    const [modal, openModal, closeModal] = useOpenState();
    const { errorToast, successToast, removeAllToasts } = useToasts();
    const { activeCompany, isCompanyAdmin, companiesLoaded } = useCompany();
    const { refreshStatistics, updateJobs, moveJob } = useDashboard();
    const readonly = !isCompanyAdmin();
    const dataRef = useStateRef(data);
    const [currentPage, setCurrentPage] = useMutableRef(1);
    const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
    const isFirstRender = useOnFirstRender();

    React.useEffect(() => {
      if (search !== filterValue) {
        onFilterChange(search);
      }
    }, [search]);

    const fetchCustomers = async (page, silent = false) => {
      if (!silent) setLoading(true);

      cancelPreviousRequest();

      try {
        const top = page * perPage;
        const skip = 0;


        const response = await axios.get(
          `/customers/with-jobs/?$top=${top}&$skip=${skip}&$cid=${activeCompany}&$status=${activeStatus}&$view=card`,
          {
            cancelToken: newCancelToken(),
            params: {
              $search: advancedSearchOpen ? buildFilter(search) : search,
            },
          }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotal(response.data.count);
        }
      } catch (error) {
        if (silent || isCancel(error)) return;
        errorToast({ error, default: 'Error: Fetch Customers.' });
      }

      setLoading(false);
    };

    const handleFilter = async (value) => {
      setLoading(true);
      setData([]);
      setSearch(value);

      cancelPreviousRequest();
      try {
        const skip = 0;
        const response = await axios.get(
          `/customers/with-jobs/?$top=${perPage}&$skip=${skip}&$cid=${activeCompany}&$status=${activeStatus}&$view=card`,
          {
            cancelToken: newCancelToken(),
            params: {
              $search: advancedSearchOpen ? buildFilter(value) : value,
            },
          }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotal(response.data.count);
          refreshStatistics(value);
        }
      } catch (error) {
        if (isCancel(error)) return;
        errorToast({ error, default: 'Error: Filter Customers.' });
      }

      setLoading(false);
    };

    const refresh = ({ newCount, silent = false } = {}) => {
      if (newCount > 0) {
        const updatedPage = getNumberOfPages(newCount, perPage);
        const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
        if (recalculatedPage !== currentPage.current) {
          setCurrentPage(recalculatedPage);
        }
      }

      fetchData(currentPage.current, silent);
      refreshStatistics(search);
    };

    const archiveCustomer = async (customer) => {
      try {
        const response = await axios.post(`/customers/${customer.id}/archive`);

        if (response.status === 200) {
          removeAllToasts();
          successToast(`Customer “${customer.name}” has been successfully archived.`);
          refresh({ newCount: total - 1 });
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Archive Customer.' });
      }

      closeModal();
    };

    const archiveCategory = async (category) => {
      try {
        const response = await axios.post(`/jobs/categories/${category.id}/archive`);

        if (response.status === 200) {
          removeAllToasts();
          successToast(`Category “${category.name}” has been successfully archived.`);
          refresh();
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Archive Category.' });
      }

      closeModal();
    };

    const handleLoadMore = () => {
      fetchData(currentPage.current + 1);
    };

    const fetchData = (page = 1, silent = false) => {
      setCurrentPage(page);
      if (page === 1 && !silent) {
        setData([]);
      }
      fetchCustomers(page, silent);
    };

    const handleRealtimeUpdates = (eventData) => {
      // updateJobs(eventData, data, setData); // doesn't update sort order, new added jobs, nor recent email tooltip
      fetchData(currentPage.current, true);
      refreshStatistics(search);
    };

    // allow us to call defined functions from parent
    React.useImperativeHandle(ref, () => ({
      refresh,
      handleRealtimeUpdates,
    }));

    React.useEffect(() => {
      if (companiesLoaded) {
        fetchData();
      }
    }, [companiesLoaded, activeCompany]);

    React.useEffect(() => {
      if (!isFirstRender) {
        setCurrentPage(1);
        refresh();
      }
    }, [activeStatus]);

    const renderCard = React.useCallback(
      (customer) => {
        const { categories, jobs } = data?.find(({ id }) => id === customer.id);

        return (
          <Card>
            <Container flexDirection="row" justifyContent="space-between" height="auto" mb="m">
              <CardTitle>{customer.name}</CardTitle>
              <div>
                {!readonly && (
                  <DropDownMenu>
                    <DropDownMenuItem onClick={() => openModal('add-job', { customer })}>
                      <Icon icon="plus" size="12px" mr="7px" /> Add Monitor
                    </DropDownMenuItem>
                    <DropDownMenuItem onClick={() => openModal('edit-customer', { customer })}>
                      <Icon icon="edit" size="14px" mr="7px" /> Edit
                    </DropDownMenuItem>
                    <DropDownMenuItem
                      onClick={() => openModal('confirm-remove-customer', { customer })}
                    >
                      <Icon icon="trash" size="16px" mr="7px" /> Delete
                    </DropDownMenuItem>
                  </DropDownMenu>
                )}
              </div>
            </Container>
            <Container overflow="auto">
              {categories?.map((category, index) => (
                <React.Fragment key={index}>
                  <Container
                    flexDirection="row"
                    justifyContent="space-between"
                    height="auto"
                    mb="xxs"
                  >
                    <CategoryLabel>{category.name}</CategoryLabel>
                    <div>
                      {!readonly && (
                        <DropDownMenu>
                          <DropDownMenuItem
                            onClick={() => openModal('edit-category', { category })}
                          >
                            <Icon icon="edit" size="14px" mr="7px" /> Edit
                          </DropDownMenuItem>
                          <DropDownMenuItem
                            onClick={() => openModal('confirm-remove-category', { category })}
                          >
                            <Icon icon="trash" size="16px" mr="7px" /> Delete
                          </DropDownMenuItem>
                        </DropDownMenu>
                      )}
                    </div>
                  </Container>
                  {renderJobs({ customer, category, jobs })}
                </React.Fragment>
              ))}
            </Container>
          </Card>
        );
      },
      [readonly, data, openModal]
    );

    const renderJobs = ({ customer, category, jobs }) => {
      const categoryJobs =
        jobs?.filter(({ category: categoryId }) => categoryId === category.id) || [];
      const [, drop] = useDrop(
        () => ({
          accept: 'job',
          drop(item) {
            const target = { customer, category };
            moveJob(item, target, dataRef.current, setData);
            return undefined;
          },
        }),
        [moveJob]
      );

      return (
        <Container
          ref={drop}
          flexDirection="row"
          height="auto"
          minHeight={categoryJobs.length === 0 ? '20px' : 'auto'}
          mb="m"
          wrapped
        >
          {categoryJobs.map((job, index) => (
            <JobDot
              key={index}
              job={job}
              onTooltipReadMoreClick={() => openModal('view-email', { email: job.lastEmail })}
              isDraggable={!readonly}
              hasTooltip
            />
          ))}
        </Container>
      );
    };

    return (
      <>
        <CardGrid
          data={data}
          total={total}
          loading={loading}
          headerActions={headerActions}
          headerDisposition="actions-first"
          cardsPerRow={4}
          cardRenderer={renderCard}
          filterPlaceholder="Search"
          filterValue={search}
          openMobileFilter={openMobileFilter}
          onFilter={handleFilter}
          advancedSearch
          advancedSearchOpen={advancedSearchOpen}
          setAdvancedSearchOpen={setAdvancedSearchOpen}
          onLoadMore={handleLoadMore}
          currentPage={currentPage.current}
          girdTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))"
        />
        <Modal
          isOpen={modal.isOpen('add-customer', 'edit-customer', 'add-category', 'edit-category')}
          onRequestClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          adjustPosition={!isFullScreenActive}
          top="25%"
          width="40%"
          maxWidth="650px"
        >
          {modal.isOpen('add-customer') && <AddCustomer onClose={closeModal} onSuccess={refresh} />}
          {modal.isOpen('edit-customer') && (
            <EditCustomer
              customer={modal.params?.customer}
              onClose={closeModal}
              onSuccess={refresh}
            />
          )}
          {modal.isOpen('add-category') && <AddCategory onClose={closeModal} onSuccess={refresh} />}
          {modal.isOpen('edit-category') && (
            <EditCategory
              category={modal.params?.category}
              onClose={closeModal}
              onSuccess={refresh}
            />
          )}
        </Modal>
        <Modal
          isOpen={modal.isOpen('add-job')}
          onRequestClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          adjustPosition={!isFullScreenActive}
          top="7%"
          width="40%"
          minWidth="700px"
          resize="horizontal"
        >
          {modal.isOpen('add-job') && (
            <AddJob customer={modal.params?.customer} onClose={closeModal} onSuccess={refresh} />
          )}
        </Modal>
        <ConfirmationModal
          show={modal.isOpen('confirm-remove-customer')}
          onClose={closeModal}
          onConfirm={() => archiveCustomer(modal.params?.customer)}
          adjustPosition={!isFullScreenActive}
          confirmButtonText="Archive"
        >
          <Text>
            {modal?.params?.customer?.name} and all its monitors will be archived, if you truly want
            to delete it, you should do so from the archives.
          </Text>
        </ConfirmationModal>
        <ConfirmationModal
          show={modal.isOpen('confirm-remove-category')}
          onClose={closeModal}
          onConfirm={() => archiveCategory(modal.params?.category)}
          adjustPosition={!isFullScreenActive}
          confirmButtonText="Archive"
        >
          <Text>
            {modal?.params?.category?.name} and all its monitors will be archived, if you truly want
            to delete it, you should do so from the archives.
          </Text>
        </ConfirmationModal>
        <EmailModal
          show={modal.isOpen('view-email')}
          onClose={closeModal}
          email={modal.params?.email}
        />
        {(!activeCompany || !readonly) && (
          <FloatingMenuButton position="fixed" right="1em" bottom="1em">
            <FloatingMenuChildButton onClick={() => openModal('add-customer')}>
              Add Customer
            </FloatingMenuChildButton>
            <FloatingMenuChildButton onClick={() => openModal('add-category')}>
              Add Category
            </FloatingMenuChildButton>
          </FloatingMenuButton>
        )}
      </>
    );
  }
);
